import { combineReducers } from "@reduxjs/toolkit";

import type { BookmarksAction } from "domains/bookmarks/actionTypes";
import { BookmarkActionTypes } from "domains/bookmarks/actionTypes";
import type { ClusterDigDeeperAction } from "domains/clusters/actionTypes";
import { ClusterActionType } from "domains/clusters/actionTypes";
import type { LatestKnowledgeAction } from "domains/monitor/latestKnowledge/actionTypes";
import { LatestKnowledgeActionType } from "domains/monitor/latestKnowledge/actionTypes";
import type { PinAddRejectAction } from "domains/pinAddReject/actionTypes";
import { PinAddRejectActionTypes } from "domains/pinAddReject/actionTypes";
import type { KnowledgeItemAction } from "entities/knowledge/actionTypes";
import { KnowledgeActionTypes } from "entities/knowledge/actionTypes";
import type { KnowledgeResponse, KnowledgeState, Source } from "entities/knowledge/types";
import { ContentType, KnowledgeType } from "entities/knowledge/types";
import type { KnowledgeDataFetchForTopicSuccessAction } from "entities/knowledgeData/actionTypes";
import { KnowledgeDataActionTypes } from "entities/knowledgeData/actionTypes";
import type { KnowledgeFeedAction } from "entities/knowledgeFeed/actionTypes";
import { KnowledgeFeedActionTypes } from "entities/knowledgeFeed/actionTypes";
import type { PublicationAction } from "entities/publication/actionTypes";
import { PublicationActionTypes } from "entities/publication/actionTypes";
import { addUserChangeReset } from "entities/user/utils";
import type { VideosFetchForTopicSuccessAction } from "entities/videos/actionTypes";
import { VideoActionTypes } from "entities/videos/actionTypes";

const initialState: KnowledgeState = {
  byId: {},
};

type Action =
  | KnowledgeItemAction
  | KnowledgeFeedAction
  | PublicationAction
  | LatestKnowledgeAction
  | ClusterDigDeeperAction
  | BookmarksAction
  | VideosFetchForTopicSuccessAction
  | KnowledgeDataFetchForTopicSuccessAction
  | PinAddRejectAction;

export const getKnowledgeType = (source: Source): KnowledgeType => {
  switch (source.content_type) {
    case ContentType.data:
      return KnowledgeType.data;
    case ContentType.video:
      return KnowledgeType.video;
    case ContentType.publication:
      return KnowledgeType.publication;
    default:
      return KnowledgeType.publication;
  }
};

const reduceData = (state: KnowledgeState["byId"], items: KnowledgeResponse[]): KnowledgeState["byId"] =>
  items.reduce(
    (byId, knowledge) => ({
      ...byId,
      [knowledge.id]: {
        ...state[knowledge.id],
        ...knowledge,
        knowledgeType: getKnowledgeType(knowledge.source),
      },
    }),
    state,
  );

const byId = (state = initialState.byId, action: Action): KnowledgeState["byId"] => {
  switch (action.type) {
    case ClusterActionType.FETCH_DIG_DEEPER_FULFILLED:
    case LatestKnowledgeActionType.FETCH_FULFILLED: {
      return reduceData(state, action.payload);
    }
    case VideoActionTypes.FETCH_FOR_TOPIC_SUCCESS:
    case PublicationActionTypes.FETCH_FOR_TOPIC_SUCCESS:
    case KnowledgeFeedActionTypes.FETCH_FOR_TOPIC_SUCCESS:
    case KnowledgeDataActionTypes.FETCH_FOR_TOPIC_SUCCESS: {
      return reduceData(state, action.payload.data);
    }
    case KnowledgeActionTypes.FETCH_FOR_ID: {
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          isPending: true,
          requestError: false,
        },
      };
    }
    case KnowledgeActionTypes.FETCH_FOR_ID_SUCCESS: {
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          ...action.payload,
          // the /knowledge/[id] response has an is_pinned field but is always null, so we will not override it in the state
          is_pinned: state[action.meta.id].is_pinned,
          knowledgeType: getKnowledgeType(action.payload.source),
          isPending: false,
        },
      };
    }
    case KnowledgeActionTypes.FETCH_FOR_ID_FAILURE: {
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          isPending: false,
          requestError: true,
        },
      };
    }
    case BookmarkActionTypes.BOOKMARK_TOGGLE_FULFILLED: {
      if (!state[action.meta.id]) {
        return state;
      }
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          isBookmarkPending: false,
          is_bookmarked: action.meta.isBookmarked,
        },
      };
    }
    case PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE_FULFILLED: {
      return {
        ...state,
        [action.meta.knowledgeId]: {
          ...state[action.meta.knowledgeId],
          is_pinned: action.meta.isPinned,
        },
      };
    }
    default:
      return state;
  }
};

export const knowledgeReducer = combineReducers({
  byId: addUserChangeReset(byId, initialState.byId),
});
